// ============================================================================
// oddsSports.ts — the single source of truth for EVERY odds-driven sport we
// model (everything except soccer, which has its own dedicated pipeline).
//
// Each entry drives: the /:slug-predictions page family + per-market sub-pages,
// the SEO helpers, the sitemap generator, the build-time prerenderer, the
// catalog-driven ingester (fetch-us-sports-odds), the model generator and the
// nav. Add a league here and it lights up end-to-end.
// ============================================================================

export type UsSportSlug = string;

export type MarketShape = "h2h_spread_total" | "h2h_only" | "outrights";

export type SportCategory =
  | "american-football"
  | "basketball"
  | "baseball"
  | "hockey"
  | "combat"
  | "cricket"
  | "rugby"
  | "aussie-rules"
  | "handball"
  | "lacrosse"
  | "tennis"
  | "golf";

export type MarketType = "moneyline" | "spread" | "total" | "outright";

export interface UsSportFaq {
  question: string;
  answer: string;
}

export interface UsSportMarket {
  /** URL sub-slug, e.g. "spread" → /nfl-predictions/spread. */
  slug: string;
  label: string;
  /** DB market_type value written by the model. */
  marketType: MarketType;
  h1: string;
  title: string;
  description: string;
  intro: string;
  keywords: string;
}

export interface UsSport {
  slug: UsSportSlug;
  /** Value stored in us_sports_* tables (same as slug). */
  sport: UsSportSlug;
  /** The Odds API sport key. */
  oddsApiKey: string;
  league: string;
  label: string;
  category: SportCategory;
  marketShape: MarketShape;
  /** Odds API regions to request for this league. */
  regions: string;
  /** Whether The Odds API exposes a scores endpoint for results/form. */
  hasScores: boolean;
  h1: string;
  title: string;
  description: string;
  intro: string;
  keywords: string;
  /** Rough season months (1-12). Empty means year-round / tournament-driven. */
  seasonMonths: number[];
  faqs: UsSportFaq[];
  markets: UsSportMarket[];
}

const YEAR_ROUND = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/** Spread market label varies by sport (run line, puck line, handicap…). */
const SPREAD_LABELS: Partial<Record<SportCategory, string>> = {
  baseball: "Run Line",
  hockey: "Puck Line",
  "american-football": "Against the Spread",
  basketball: "Against the Spread",
};

/** Total market label varies by sport. */
const TOTAL_LABELS: Partial<Record<SportCategory, string>> = {
  baseball: "Run Totals (O/U)",
  hockey: "Goal Totals (O/U)",
};

function marketsFor(label: string, category: SportCategory, shape: MarketShape): UsSportMarket[] {
  const l = label.toLowerCase();
  const moneyline: UsSportMarket = {
    slug: "moneyline",
    label: category === "combat" || category === "tennis" ? "Match Winner" : "Moneyline",
    marketType: "moneyline",
    h1: `${label} Moneyline Predictions`,
    title: `${label} Moneyline Predictions & Picks | Statlign`,
    description: `${label} moneyline predictions and best-value picks from live odds and recent form, with model win probability and edge on every game.`,
    intro: `${label} moneyline predictions for every game, ranking each side by model win probability versus the market price to surface outright value.`,
    keywords: `${l} moneyline predictions, ${l} picks, ${l} best bets, ${l} odds`,
  };
  if (shape === "h2h_only") return [moneyline];
  if (shape === "outrights") {
    return [
      {
        slug: "outright",
        label: "Outright Winner",
        marketType: "outright",
        h1: `${label} Outright Winner Predictions`,
        title: `${label} Outright Winner Predictions & Odds | Statlign`,
        description: `${label} outright winner predictions — de-vigged field odds ranked to find the best-value contenders, with model probability and edge.`,
        intro: `${label} outright predictions ranking the field by de-vigged win probability against the market price to find tournament value.`,
        keywords: `${l} winner predictions, ${l} outright odds, ${l} futures, ${l} picks`,
      },
    ];
  }
  return [
    moneyline,
    {
      slug: "spread",
      label: SPREAD_LABELS[category] ?? "Spread / Handicap",
      marketType: "spread",
      h1: `${label} ${SPREAD_LABELS[category] ?? "Spread"} Predictions`,
      title: `${label} Spread Predictions | Statlign`,
      description: `${label} handicap/spread predictions with the market line, model edge and confidence for every upcoming game.`,
      intro: `${label} spread predictions using the posted line and recent form to find the best value handicap pick on every game.`,
      keywords: `${l} spread predictions, ${l} handicap picks, ${l} lines, ${l} against the spread`,
    },
    {
      slug: "total",
      label: TOTAL_LABELS[category] ?? "Over/Under Totals",
      marketType: "total",
      h1: `${label} Over/Under Total Predictions`,
      title: `${label} Over/Under Total Predictions | Statlign`,
      description: `${label} over/under totals predictions with the posted line, projected total and value edge for every game on the slate.`,
      intro: `${label} over/under predictions comparing our projected total to the posted line to flag the strongest totals value.`,
      keywords: `${l} over under predictions, ${l} totals picks, ${l} o/u, ${l} game totals`,
    },
  ];
}

function genericFaqs(label: string, shape: MarketShape): UsSportFaq[] {
  const marketsLine =
    shape === "outrights"
      ? "Outright tournament winner odds are de-vigged and ranked to find value contenders."
      : shape === "h2h_only"
        ? "Match winner (moneyline) markets for every scheduled event."
        : "Moneyline, spread/handicap and over/under totals for every scheduled game.";
  return [
    {
      question: `How are Statlign's ${label} predictions made?`,
      answer:
        `We pull live ${label} odds, remove the bookmaker margin to get true implied probabilities, then adjust for recent form to surface the biggest positive value edge. Every pick shows the model probability and edge. No pick is guaranteed — bet responsibly.`,
    },
    {
      question: `What ${label} markets do you cover?`,
      answer: marketsLine,
    },
  ];
}

interface SportSeed {
  slug: string;
  key: string;
  label: string;
  league?: string;
  category: SportCategory;
  shape?: MarketShape;
  regions?: string;
  hasScores?: boolean;
  seasonMonths?: number[];
}

function mk(seed: SportSeed): UsSport {
  const shape: MarketShape = seed.shape ?? "h2h_spread_total";
  const label = seed.label;
  const l = label.toLowerCase();
  const marketsBlurb =
    shape === "outrights"
      ? "outright winner"
      : shape === "h2h_only"
        ? "match winner"
        : "moneyline, spread and totals";
  return {
    slug: seed.slug,
    sport: seed.slug,
    oddsApiKey: seed.key,
    league: seed.league ?? label,
    label,
    category: seed.category,
    marketShape: shape,
    regions: seed.regions ?? "us,uk,eu,au",
    hasScores: seed.hasScores ?? shape !== "outrights",
    h1: `${label} Predictions & Picks`,
    title: `${label} Predictions, Picks & Odds | Statlign`,
    description: `${label} predictions and picks — ${marketsBlurb} — built from live ${label} odds and recent form with model win probability, value edge and confidence.`,
    intro: `Data-driven ${label} predictions for every event on the slate. Each pick blends the live market line with recent form to surface genuine value on ${marketsBlurb}, with the model probability and edge shown for full transparency.`,
    keywords: `${l} predictions, ${l} picks, ${l} odds, ${l} best bets, ${l} tips`,
    seasonMonths: seed.seasonMonths ?? YEAR_ROUND,
    faqs: genericFaqs(label, shape),
    markets: marketsFor(label, seed.category, shape),
    ...(HUB_SEO[seed.slug] ?? {}),
  };
}

/** Hub title/description/H1 tuned to each sport's primary search phrase. */
const HUB_SEO: Record<string, { h1: string; title: string; description: string }> = {
  boxing: {
    h1: "Boxing Picks Tonight & Predictions",
    title: "Boxing Picks Tonight — Fight Predictions & Odds | Statlign",
    description: "Boxing picks tonight: every fight on the card with model probability, value edge and method-of-victory analysis, built from live boxing odds.",
  },
  mma: {
    h1: "UFC Predictions & MMA Picks",
    title: "UFC Predictions — MMA Picks for This Weekend's Card | Statlign",
    description: "UFC predictions for every bout on the card: style matchups, takedown and control analysis, model probability and value edge from live MMA odds.",
  },
  nfl: {
    h1: "NFL Spread Picks & Predictions",
    title: "NFL Spread Picks — Against the Spread Predictions | Statlign",
    description: "NFL spread picks against the spread, with key-number analysis, home-field adjustment, model probability and value edge from live NFL lines.",
  },
  nba: {
    h1: "NBA Totals, Picks & Predictions",
    title: "NBA Totals — Over/Under Picks & Predictions | Statlign",
    description: "NBA totals and over/under picks modelled from pace, offensive rating and rest, with model probability and value edge from live NBA lines.",
  },
};

// ---------------------------------------------------------------------------
// The catalog. Slugs for the original four are preserved for backwards compat.
// ---------------------------------------------------------------------------
const SEEDS: SportSeed[] = [
  // American football
  { slug: "nfl", key: "americanfootball_nfl", label: "NFL", category: "american-football", regions: "us", seasonMonths: [1, 2, 9, 10, 11, 12] },
  { slug: "ncaaf", key: "americanfootball_ncaaf", label: "NCAAF", category: "american-football", regions: "us", seasonMonths: [1, 8, 9, 10, 11, 12] },
  { slug: "cfl", key: "americanfootball_cfl", label: "CFL", category: "american-football", regions: "us" },
  { slug: "ufl", key: "americanfootball_ufl", label: "UFL", category: "american-football", regions: "us" },
  // Basketball
  { slug: "nba", key: "basketball_nba", label: "NBA", category: "basketball", regions: "us", seasonMonths: [1, 2, 3, 4, 5, 6, 10, 11, 12] },
  { slug: "ncaab", key: "basketball_ncaab", label: "NCAAB", category: "basketball", regions: "us" },
  { slug: "wncaab", key: "basketball_wncaab", label: "WNCAAB", category: "basketball", regions: "us" },
  { slug: "wnba", key: "basketball_wnba", label: "WNBA", category: "basketball", regions: "us" },
  { slug: "euroleague", key: "basketball_euroleague", label: "Basketball Euroleague", category: "basketball", regions: "eu,uk" },
  { slug: "nbl", key: "basketball_nbl", label: "NBL", category: "basketball", regions: "au" },
  // Baseball
  { slug: "mlb", key: "baseball_mlb", label: "MLB", category: "baseball", regions: "us", seasonMonths: [3, 4, 5, 6, 7, 8, 9, 10] },
  { slug: "npb", key: "baseball_npb", label: "NPB", category: "baseball", regions: "us,eu" },
  { slug: "kbo", key: "baseball_kbo", label: "KBO League", category: "baseball", regions: "us,eu" },
  { slug: "ncaa-baseball", key: "baseball_ncaa", label: "NCAA Baseball", category: "baseball", regions: "us" },
  // Hockey
  { slug: "nhl", key: "icehockey_nhl", label: "NHL", category: "hockey", regions: "us", seasonMonths: [1, 2, 3, 4, 5, 6, 10, 11, 12] },
  { slug: "ahl", key: "icehockey_ahl", label: "AHL", category: "hockey", regions: "us" },
  { slug: "shl", key: "icehockey_sweden_hockey_league", label: "SHL", category: "hockey", regions: "eu,uk" },
  { slug: "liiga", key: "icehockey_liiga", label: "Finnish Liiga", category: "hockey", regions: "eu,uk" },
  // Combat
  { slug: "mma", key: "mma_mixed_martial_arts", label: "MMA", category: "combat", shape: "h2h_only", regions: "us,uk,eu", hasScores: false },
  { slug: "boxing", key: "boxing_boxing", label: "Boxing", category: "combat", shape: "h2h_only", regions: "us,uk,eu", hasScores: false },
  // Aussie rules / rugby / handball / lacrosse
  { slug: "afl", key: "aussierules_afl", label: "AFL", category: "aussie-rules", regions: "au" },
  { slug: "nrl", key: "rugbyleague_nrl", label: "NRL", category: "rugby", regions: "au,uk" },
  { slug: "six-nations", key: "rugbyunion_six_nations", label: "Six Nations", category: "rugby", shape: "h2h_only", regions: "uk,eu" },
  { slug: "handball-bundesliga", key: "handball_germany_bundesliga", label: "Handball Bundesliga", category: "handball", regions: "eu,uk" },
  { slug: "pll", key: "lacrosse_pll", label: "Premier Lacrosse League", category: "lacrosse", regions: "us" },
  { slug: "ncaa-lacrosse", key: "lacrosse_ncaa", label: "NCAA Lacrosse", category: "lacrosse", regions: "us" },
  // Cricket (match winner)
  { slug: "ipl", key: "cricket_ipl", label: "IPL", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "big-bash", key: "cricket_big_bash", label: "Big Bash", category: "cricket", shape: "h2h_only", regions: "au,uk" },
  { slug: "the-hundred", key: "cricket_the_hundred", label: "The Hundred", category: "cricket", shape: "h2h_only", regions: "uk" },
  { slug: "t20-blast", key: "cricket_t20_blast", label: "T20 Blast", category: "cricket", shape: "h2h_only", regions: "uk" },
  { slug: "psl", key: "cricket_psl", label: "Pakistan Super League", category: "cricket", shape: "h2h_only", regions: "uk,au" },
  { slug: "cpl", key: "cricket_caribbean_premier_league", label: "Caribbean Premier League", category: "cricket", shape: "h2h_only", regions: "uk,us" },
  { slug: "odi", key: "cricket_odi", label: "One Day Internationals", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "test-cricket", key: "cricket_test_match", label: "Test Matches", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "t20-internationals", key: "cricket_international_t20", label: "International Twenty20", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "t20-world-cup", key: "cricket_t20_world_cup", label: "T20 World Cup", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "icc-world-cup", key: "cricket_icc_world_cup", label: "ICC World Cup", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "womens-t20-world-cup", key: "cricket_t20_world_cup_womens", label: "T20 Women's World Cup", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "asia-cup", key: "cricket_asia_cup", label: "Asia Cup", category: "cricket", shape: "h2h_only", regions: "uk,eu,au" },
  // Tennis (match winner)
  { slug: "tennis-atp-aus-open", key: "tennis_atp_aus_open_singles", label: "ATP Australian Open", category: "tennis", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "tennis-atp-french-open", key: "tennis_atp_french_open", label: "ATP French Open", category: "tennis", shape: "h2h_only", regions: "uk,eu" },
  { slug: "tennis-atp-wimbledon", key: "tennis_atp_wimbledon", label: "ATP Wimbledon", category: "tennis", shape: "h2h_only", regions: "uk,eu" },
  { slug: "tennis-atp-us-open", key: "tennis_atp_us_open", label: "ATP US Open", category: "tennis", shape: "h2h_only", regions: "us,uk,eu" },
  { slug: "tennis-wta-aus-open", key: "tennis_wta_aus_open_singles", label: "WTA Australian Open", category: "tennis", shape: "h2h_only", regions: "uk,eu,au" },
  { slug: "tennis-wta-french-open", key: "tennis_wta_french_open", label: "WTA French Open", category: "tennis", shape: "h2h_only", regions: "uk,eu" },
  { slug: "tennis-wta-wimbledon", key: "tennis_wta_wimbledon", label: "WTA Wimbledon", category: "tennis", shape: "h2h_only", regions: "uk,eu" },
  { slug: "tennis-wta-us-open", key: "tennis_wta_us_open", label: "WTA US Open", category: "tennis", shape: "h2h_only", regions: "us,uk,eu" },
  // Golf (outrights)
  { slug: "masters", key: "golf_masters_tournament_winner", label: "Masters Tournament", category: "golf", shape: "outrights", regions: "us,uk,eu", hasScores: false },
  { slug: "pga-championship", key: "golf_pga_championship_winner", label: "PGA Championship", category: "golf", shape: "outrights", regions: "us,uk,eu", hasScores: false },
  { slug: "the-open", key: "golf_the_open_championship_winner", label: "The Open Championship", category: "golf", shape: "outrights", regions: "us,uk,eu", hasScores: false },
  { slug: "us-open-golf", key: "golf_us_open_winner", label: "US Open (Golf)", category: "golf", shape: "outrights", regions: "us,uk,eu", hasScores: false },
];

export const US_SPORTS: UsSport[] = SEEDS.map(mk);

/** Fast lookups. */
const BY_SLUG = new Map(US_SPORTS.map((s) => [s.slug, s]));
const BY_KEY = new Map(US_SPORTS.map((s) => [s.oddsApiKey, s]));

export function findUsSport(slug: string): UsSport | undefined {
  return BY_SLUG.get(slug);
}

export function findUsSportByKey(key: string): UsSport | undefined {
  return BY_KEY.get(key);
}

const DEDICATED_SPORT_OG_IMAGES = new Set<UsSportSlug>([
  "boxing",
  "mma",
  "mlb",
  "nba",
  "nfl",
  "nhl",
  "handball-bundesliga",
  "tennis-wta-aus-open",
  "tennis-wta-wimbledon",
]);

/** Root-relative social preview image for a sport, with a guaranteed generic fallback. */
export function ogImageForUsSport(slug: UsSportSlug): string {
  return DEDICATED_SPORT_OG_IMAGES.has(slug) ? `/og-${slug}.jpg` : "/og-image.jpg";
}

export function findUsSportMarket(sport: UsSport, marketSlug: string): UsSportMarket | undefined {
  return sport.markets.find((m) => m.slug === marketSlug);
}

/** Whether a sport is currently in (or near) its season, by UTC month. */
export function isInSeason(sport: UsSport, now = new Date()): boolean {
  if (!sport.seasonMonths.length) return true;
  return sport.seasonMonths.includes(now.getUTCMonth() + 1);
}

/** Group the catalog by category for grouped navigation menus. */
export const SPORT_CATEGORY_LABELS: Record<SportCategory, string> = {
  "american-football": "American Football",
  basketball: "Basketball",
  baseball: "Baseball",
  hockey: "Ice Hockey",
  combat: "Combat Sports",
  cricket: "Cricket",
  rugby: "Rugby",
  "aussie-rules": "Aussie Rules",
  handball: "Handball",
  lacrosse: "Lacrosse",
  tennis: "Tennis",
  golf: "Golf",
};

export const SPORTS_BY_CATEGORY: { category: SportCategory; label: string; sports: UsSport[] }[] =
  (Object.keys(SPORT_CATEGORY_LABELS) as SportCategory[])
    .map((category) => ({
      category,
      label: SPORT_CATEGORY_LABELS[category],
      sports: US_SPORTS.filter((s) => s.category === category),
    }))
    .filter((g) => g.sports.length > 0);

/** All sport + market routes for the sitemap and prerenderer. */
/**
 * Discovery surfaces every sport now carries, matching what football already
 * had: a fixture index plus time-based landings and a team directory. These are
 * how crawlers reach each sport's individual match pages.
 */
export const SPORT_DISCOVERY_SUBROUTES = [
  "fixtures",
  "today",
  "tomorrow",
  "weekend",
  "teams",
] as const;

export const US_SPORT_ROUTES: string[] = US_SPORTS.flatMap((s) => [
  `/${s.slug}-predictions`,
  ...s.markets.map((m) => `/${s.slug}-predictions/${m.slug}`),
  ...SPORT_DISCOVERY_SUBROUTES.map((sub) => `/${s.slug}-predictions/${sub}`),
]);

/** Alias for readability in newer call sites. */
export const SPORT_ROUTES = US_SPORT_ROUTES;
